// src/context/SettingsContext.js
import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { fetchSiteSettings } from "../store/custom-actions/settings-actions/siteSettingsSlicer";
import {
  applySiteSettings,
  cacheSiteSettings,
} from "../utils/applySiteSettings";

const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const cached = localStorage.getItem("siteSettings");
        const parsedCached = cached ? JSON.parse(cached) : null;

        if (parsedCached) {
          setSettings(parsedCached);
          applySiteSettings(parsedCached);
          setLoading(false);
        }

        const result = await dispatch(fetchSiteSettings());
        const data = result?.payload;

        if (data && JSON.stringify(data) !== JSON.stringify(parsedCached)) {
          setSettings(data);
          cacheSiteSettings(data);
          applySiteSettings(data);
        }
      } catch (error) {
        console.error("❌ Failed to load settings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [dispatch]);

  const getSetting = (key, fallback = null) => {
    if (!settings || typeof settings !== "object") return fallback;
    return key in settings ? settings[key] : fallback;
  };

  const contextValue = useMemo(
    () => ({ settings, loading, getSetting }),
    [settings, loading]
  );

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
